import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, getOrgChart, getOrganizationIdentity, type OrgChart, type OrgChartNode, type OrganizationIdentity } from '../../api/client';
import { Icon } from '../../components/Icon';
import { useAuth } from '../../auth/AuthContext';
import { Pending } from './shared';

interface DepartmentRow { id: string; code: string; name: string }

/**
 * Administrative reporting only. Functional assignments describe who directs a
 * piece of work; they are shown on the profile, never in this tree, because
 * conflating the two is how an org chart starts asserting authority nobody granted.
 */
function ChartNode({ node, depth, query, expanded, toggle }: {
  node: OrgChartNode; depth: number; query: string;
  expanded: Set<string>; toggle: (id: string) => void;
}) {
  const isOpen = expanded.has(node.employeeId);
  const hasReports = node.directReports.length > 0;
  const match = query.length > 1 &&
    (node.name.toLowerCase().includes(query.toLowerCase()) ||
     node.designation.toLowerCase().includes(query.toLowerCase()));

  return (
    <li className="org-node">
      <div className={`org-node-card${match ? ' org-node-match' : ''}`} style={{ marginLeft: depth * 4 }}>
        <button
          type="button"
          className="org-node-toggle"
          onClick={() => toggle(node.employeeId)}
          aria-label={hasReports ? (isOpen ? 'Collapse' : 'Expand') : 'No direct reports'}
          disabled={!hasReports}
        >
          {hasReports ? (isOpen ? '−' : '+') : '·'}
        </button>
        <div className="org-node-body">
          <Link className="record-link" to={`/employees/${node.employeeId}`}>{node.name}</Link>
          <span className="org-node-role"><Pending>{node.designation}</Pending></span>
        </div>
        <span className="mono org-node-code">{node.employeeCode}</span>
        {node.employmentType === 'intern' && <span className="org-chip">Intern</span>}
        {hasReports && <span className="org-chip org-chip-quiet">{node.directReports.length} report{node.directReports.length === 1 ? '' : 's'}</span>}
      </div>
      {hasReports && isOpen && (
        <ul className="org-children">
          {node.directReports.map((child) => (
            <ChartNode key={child.employeeId} node={child} depth={depth + 1} query={query} expanded={expanded} toggle={toggle} />
          ))}
        </ul>
      )}
    </li>
  );
}

function collectIds(nodes: OrgChartNode[], into: Set<string> = new Set()): Set<string> {
  for (const n of nodes) { into.add(n.employeeId); collectIds(n.directReports, into); }
  return into;
}

export function OrgOverview() {
  const { session } = useAuth();
  const [chart, setChart] = useState<OrgChart | null>(null);
  const [identity, setIdentity] = useState<OrganizationIdentity | null>(null);
  const [departments, setDepartments] = useState<DepartmentRow[]>([]);
  const [query, setQuery] = useState('');
  const [showFormer, setShowFormer] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getOrgChart(),
      api<{ items: DepartmentRow[] }>('/api/v1/departments').catch(() => ({ items: [] })),
      getOrganizationIdentity().catch(() => null),
    ])
      .then(([c, d, ident]) => {
        setChart(c);
        setDepartments(d.items);
        setIdentity(ident);
        setExpanded(collectIds(c.activeHierarchy));
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const headcount = useMemo(() => collectIds(chart?.activeHierarchy ?? []).size, [chart]);

  // The tenant's organisation name is shown as stored. If it does not look like
  // ABNY's legal identity, that is surfaced as a question for management rather
  // than corrected here — renaming a legal entity is not a UI concern.
  const orgName = session?.tenant.name ?? '';

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow"><Icon name="departments" size="xs" /><span>Organization</span></span>
          <h1>Structure &amp; Reporting Hierarchy</h1>
        </div>
        <div className="toolbar">
          <input aria-label="Search the chart" placeholder="Search name or designation…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <button type="button" onClick={() => setExpanded(collectIds(chart?.activeHierarchy ?? []))}>Expand all</button>
          <button type="button" onClick={() => setExpanded(new Set())}>Collapse all</button>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-head">
          <strong>Organization identity</strong>
          {identity && (
            <span className={`org-chip org-chip-${identity.status === 'confirmed' ? 'active' : 'pending'}`}>
              {identity.status === 'confirmed' ? 'Confirmed' : 'Legal Identity Pending Confirmation'}
            </span>
          )}
        </div>
        <dl className="org-jd-meta">
          <div>
            <dt>Internal organization name</dt>
            <dd>{identity?.internalName ?? orgName}</dd>
          </div>
          <div>
            <dt>Legal name</dt>
            <dd>{identity?.identity.legal_name ?? <Pending />}</dd>
          </div>
          <div><dt>PAN</dt><dd>{identity?.identity.pan ?? <Pending />}</dd></div>
          <div><dt>GSTIN</dt><dd>{identity?.identity.gstin ?? <Pending />}</dd></div>
          <div><dt>CIN</dt><dd>{identity?.identity.cin ?? <Pending />}</dd></div>
          <div><dt>Registered address</dt><dd>{identity?.identity.registered_address ?? <Pending />}</dd></div>
        </dl>
        {identity && identity.status !== 'confirmed' && (
          <div className="banner warn" style={{ margin: '10px 0 0' }}>
            <strong>Legal Identity Pending Confirmation.</strong>{' '}
            {identity.missingFields.length} field{identity.missingFields.length === 1 ? '' : 's'} unsupplied.
            {identity.storedOrganizationRecord && (
              <> The organization row stored on this tenant reads “{identity.storedOrganizationRecord.legalName}”
              ({identity.storedOrganizationRecord.country}) — demo data, shown here only so the conflict is visible.
              It has not been renamed, and it is not ABNY's identity.</>
            )}
          </div>
        )}
        <p className="muted org-footnote">
          The internal organization name is what the company calls itself. The legal identity is what appears on
          documents and filings. They are different records and the system never substitutes one for the other.
        </p>
      </div>

      {error && <div className="banner error" style={{ marginBottom: 14 }}>{error}</div>}

      {chart && chart.orphans.length > 0 && (
        <div className="banner warn" style={{ marginBottom: 14 }}>
          <strong>{chart.orphans.length} employee{chart.orphans.length === 1 ? '' : 's'} reference a manager who is not active.</strong>{' '}
          {chart.orphans.map((o) => o.name).join(', ')}. Flagged, not repaired.
        </div>
      )}

      <div className="org-grid">
        <div className="panel">
          <div className="panel-head">
            <strong>Administrative reporting</strong>
            <span className="muted" style={{ fontSize: 12 }}>{headcount} active {headcount === 1 ? 'person' : 'people'}</span>
          </div>
          {loading ? (
            <div className="empty"><strong>Loading the hierarchy…</strong></div>
          ) : !chart || chart.activeHierarchy.length === 0 ? (
            <div className="empty">
              <strong>No active employees</strong>
              <p>Run the organizational register seed, then reload.</p>
            </div>
          ) : (
            <ul className="org-tree">
              {chart.activeHierarchy.map((n) => (
                <ChartNode key={n.employeeId} node={n} depth={0} query={query} expanded={expanded} toggle={toggle} />
              ))}
            </ul>
          )}
        </div>

        <div className="org-side">
          <div className="panel">
            <div className="panel-head"><strong>Departments</strong></div>
            {departments.length === 0 ? (
              <div className="empty"><strong>No departments recorded</strong></div>
            ) : (
              <ul className="org-plain-list">
                {departments.map((d) => (
                  <li key={d.id}>
                    <span className="mono org-node-code">{d.code}</span>
                    <span>{d.name}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="muted org-footnote">
              A department may hold no employees. Existing is not the same as being staffed.
            </p>
          </div>

          <div className="panel">
            <div className="panel-head">
              <strong>Former employees</strong>
              <button type="button" onClick={() => setShowFormer((v) => !v)}>{showFormer ? 'Hide' : 'Show'}</button>
            </div>
            {showFormer && (
              chart && chart.formerEmployees.length > 0 ? (
                <ul className="org-plain-list">
                  {chart.formerEmployees.map((f) => (
                    <li key={f.employeeId}>
                      <strong>{f.name}</strong>
                      <span className="muted">{f.status}</span>
                    </li>
                  ))}
                </ul>
              ) : <div className="empty"><strong>No separated employees on record</strong></div>
            )}
            <p className="muted org-footnote">
              Archived and read-only. Outside the active hierarchy, never deleted.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
